/* eslint-disable @angular-eslint/component-selector */
import {
    ChangeDetectionStrategy,
    Component,
    Directive,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    TemplateRef,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';

// <editor-fold defaultstate="collapsed" desc="Cells">
@Directive({
    selector: '[kalColumn]',
    standalone: false
})
export class KalColumnDirective {
    /**
     * uniq name for this column
     */
    @Input('kalColumn') name: string;
}

/**
 * base cell
 */
@Directive()
export abstract class KalBaseCellDirective implements OnInit {
    @Input() name: string;

    protected constructor(protected readonly column: KalColumnDirective,
                          public readonly template: TemplateRef<any>) {
    }

    private setName() {
        // wait for column to be defined ( could be null in constructor )
        this.name = this.column ? this.column.name : this.name; // defaut name to columnDef.name
    }

    ngOnInit(): void {
        this.setName();
    }
}

@Directive({
    selector: '[kalHeaderCell]',
    standalone: false
})
export class KalHeaderCellDirective extends KalBaseCellDirective {
    @Input('kalHeaderCell') name: string;

    constructor(@Optional() column: KalColumnDirective,
                template: TemplateRef<any>) {
        super(column, template);
    }
}

@Directive({
    selector: '[kalFooterCell]',
    standalone: false
})
export class KalFooterCellDirective extends KalBaseCellDirective {
    @Input('kalFooterCell') name: string;

    constructor(@Optional() column: KalColumnDirective,
                template: TemplateRef<any>) {
        super(column, template);
    }
}

@Directive({
    selector: '[kalBodyCell]',
    standalone: false
})
export class KalBodyCellDirective extends KalBaseCellDirective {
    @Input('kalBodyCell') name: string;

    constructor(@Optional() column: KalColumnDirective,
                template: TemplateRef<any>) {
        super(column, template);
    }
}

// </editor-fold>


// <editor-fold defaultstate="collapsed" desc="Rows">
export abstract class KalBaseRowDirective {
    constructor(public template: TemplateRef<any>) {
    }
}

@Directive({
    selector: '[kalHeaderRowDef]',
    standalone: false
})
export class KalHeaderRowDirective extends KalBaseRowDirective {
    constructor(template: TemplateRef<any>) {
        super(template);
    }
}

@Directive({
    selector: '[kalFooterRowDef]',
    standalone: false
})
export class KalFooterRowDirective extends KalBaseRowDirective {
    constructor(template: TemplateRef<any>) {
        super(template);
    }
}

@Directive({
    selector: '[kalBodyRowDef]',
    standalone: false
})
export class KalBodyRowDirective extends KalBaseRowDirective {
    constructor(template: TemplateRef<any>) {
        super(template);
    }
}

// </editor-fold>


// <editor-fold defaultstate="collapsed" desc="Outlet">
export abstract class KalBaseOutletDirective {
    constructor(public viewContainer: ViewContainerRef) {
    }
}

@Directive({
    selector: '[kalBodyRowOutlet]',
    standalone: false
})
export class KalBodyRowOutletDirective extends KalBaseOutletDirective {
    constructor(viewContainer: ViewContainerRef) {
        super(viewContainer);
    }
}

/**
 * Provides a handle for the table to grab the view container's ng-container to insert the header.
 * @docs-private
 */
@Directive({
    selector: '[kalHeaderRowOutlet]',
    standalone: false
})
export class KalHeaderRowOutletDirective extends KalBaseOutletDirective {
    constructor(viewContainer: ViewContainerRef) {
        super(viewContainer);
    }
}

/**
 * Provides a handle for the table to grab the view container's ng-container to insert the footer.
 * @docs-private
 */
@Directive({
    selector: '[kalFooterRowOutlet]',
    standalone: false
})
export class KalFooterRowOutletDirective extends KalBaseOutletDirective {
    constructor(viewContainer: ViewContainerRef) {
        super(viewContainer);
    }
}

// </editor-fold>


// <editor-fold defaultstate="collapsed" desc="Components">
export const KAL_ROW_TEMPLATE = `<ng-container kalCellsOutlet></ng-container>`;

@Directive({
    selector: '[kalCellsOutlet]',
    standalone: false
})
export class KalCellsOutletDirective implements OnDestroy {
    /**
     * Static property containing the latest constructed instance of this class.
     * Used by the CDK table when each CdkHeaderRow and CdkRow component is created using
     * createEmbeddedView. After one of these components are created, this property will provide
     * a handle to provide that component's cells and context. After init, the CdkCellOutlet will
     * construct the cells with the provided context.
     */
    static mostRecentCellOutlet: KalCellsOutletDirective | null = null;

    /** The ordered list of cells to render within this outlet's view container */

    // cells: CdkCellDef[];

    /** The data context to be provided to each cell */
    // context: any;

    constructor(public viewContainer: ViewContainerRef) {
        KalCellsOutletDirective.mostRecentCellOutlet = this;
    }

    ngOnDestroy() {
        // If this was the last outlet being rendered in the view, remove the reference
        // from the static property after it has been destroyed to avoid leaking memory.
        if (KalCellsOutletDirective.mostRecentCellOutlet === this) {
            KalCellsOutletDirective.mostRecentCellOutlet = null;
        }
    }
}

/** Header template container that contains the cell outlet. Adds the right class and role. */
@Component({
    selector: '[kal-header-row]',
    template: KAL_ROW_TEMPLATE,
    host: {
        role: 'row',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class KalHeaderRowComponent {
}


/** Footer template container that contains the cell outlet. Adds the right class and role. */
@Component({
    selector: '[kal-footer-row]',
    template: KAL_ROW_TEMPLATE,
    host: {
        role: 'row',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class KalFooterRowComponent {
}

/** Data row template container that contains the cell outlet. Adds the right class and role. */
@Component({
    selector: '[kal-body-row]',
    template: KAL_ROW_TEMPLATE,
    host: {
        role: 'row',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class KalBodyRowComponent {
}

// </editor-fold>
